import React, { useState, useEffect } from 'react';
import { Table, Select, Spin, Button, Space, Alert, Tooltip } from 'antd';
import Icon, { LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSelector, useDispatch } from 'react-redux';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';
import useInvokeHubFunction from '@common/hooks/useInvokeHubFunction';
import { GetHubSecretsResponse, GetRecipeSecretMappingsResponse, AddRecipeSecretMappingArgs, RecipeSecretMapping, HubEnvVarInfo } from '@src/types/secretMapping_t';
import { KemuHubFunctions } from '@kemu-io/hs-types';
import styles from './SecretMappingModal.module.css';
import { ReactComponent as BookmarkIcon } from '@src/assets/img/interface/bookmark-outline.svg';
import { ReactComponent as BookmarkTicked } from '@src/assets/img/interface/bookmark-outline-ticked.svg';
import { getRecipeRequiredSecrets } from '@src/app/recipe/utils';
import { selectSecretMappingModalVisible, showSecretMappingModal, showUserProfileModal } from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';

const { Option } = Select;
type SecretMapping = RecipeSecretMapping & { secretName: string };

/**
 * Modal component for mapping recipe secrets to environment variables
 */
const SecretMappingModal: React.FC = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectSecretMappingModalVisible);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const t = useTranslation('SecretMappingModal');
  const tCommon = t.withBaseKey('CommonWords');
  const invokeHubFunction = useInvokeHubFunction();
  const recipeUuid = currentRecipe.uuid;
  const recipePoolId = currentRecipe.poolId;
  const [loading, setLoading] = useState(false);
  const [hubSecrets, setHubSecrets] = useState<Record<string, HubEnvVarInfo[]> | null>(null);
  const [secretMappings, setSecretMappings] = useState<Array<SecretMapping>>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  /**
   * Loads the required secrets for the recipe and available hub secrets
   */
  const loadData = async () => {
    if (!recipeUuid || !recipePoolId) { return; }
    
    setLoading(true);
    try {
      // Get required secrets from the recipe
      const recipeRequiredSecrets = getRecipeRequiredSecrets(recipePoolId);
      
      // Get the list of already mapped secrets for this recipe
      const mappedSecrets = await invokeHubFunction<GetRecipeSecretMappingsResponse>(KemuHubFunctions.GetRecipeSecretMappings, [{ recipeUuid }]);
      let existingMappings: Record<string, RecipeSecretMapping> = {};
      if (mappedSecrets && Array.isArray(mappedSecrets) && mappedSecrets.length > 0) {
        existingMappings = mappedSecrets[0].secrets;
      }
      
      // Get available hub secrets from the hub
      const hubData = await invokeHubFunction<GetHubSecretsResponse>(KemuHubFunctions.GetHubSecrets, [{ recipeUuid }]);
      if (hubData && Array.isArray(hubData) && hubData.length > 0) {
        const secrets = hubData[0].contexts;
        setHubSecrets(secrets);
      }

      // Create combined data structure with required secrets and existing mappings
      const combinedMappings: SecretMapping[] = recipeRequiredSecrets.map(secret => {
        const secretName = secret.name;
        const existingMapping = existingMappings[secretName];
        return {
          secretName,
          context: existingMapping?.context || '',
          envVarName: existingMapping?.envVarName || '',
          createdAt: existingMapping?.createdAt || '',
          rememberMapping: existingMapping?.rememberMapping,
          hubServiceName: secret.hubServiceName,
          isOptional: secret.optional,
        };
      });
      
      setSecretMappings(combinedMappings);
    } catch (error) {
      console.error('Failed to load secret data:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates a specific mapping
   */
  const updateMapping = (secretName: string, field: 'context' | 'envVarName' | 'rememberMapping', value: string | boolean) => {
    setSecretMappings(prevMappings => 
      prevMappings.map(mapping => 
        mapping.secretName === secretName 
          ? { ...mapping, [field]: value }
          : mapping
      )
    );
  };

  // /**
  //  * Handles remembering a mapping
  //  */
  // const handleRememberMapping = (mapping: SecretMapping) => {
  //   updateMapping(mapping.secretName, 'rememberMapping', true);
  // };

  /**
   * Gets available environment variables for a specific context
   */
  const getAvailableEnvVars = (context: string): HubEnvVarInfo[] => {
    if (!hubSecrets || !context) { return []; }
    return hubSecrets[context] || [];
  };

  /**
   * Gets available contexts from hub secrets
   */
  const getAvailableContexts = (): string[] => {
    if (!hubSecrets) { return []; }
    return Object.keys(hubSecrets);
  };

  /**
   * Handles form submission
   */
  const handleOk = async () => {
    if (!recipeUuid) { return; }

    setLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare the secrets map without createdAt for the API call
      const secretsMap: Record<string, Omit<RecipeSecretMapping, 'createdAt'>> = {};
      secretMappings.forEach(mapping => {
        if (mapping.secretName) {
          // allow secrets value mappings to be empty
          secretsMap[mapping.secretName] = {
            context: mapping.context || '',
            envVarName: mapping.envVarName || '',
            hubServiceName: mapping.hubServiceName,
            isOptional: mapping.isOptional,
            // Only services can have persistent mappings
            rememberMapping: Boolean(mapping.rememberMapping && mapping.hubServiceName),
          };
        }
      });

      const args: AddRecipeSecretMappingArgs = [{
        recipeUuid,
        secretsMap,
      }];

      await invokeHubFunction(KemuHubFunctions.AddRecipeSecretMapping, args);
      console.log('Secret mappings saved:', secretMappings);
      setSaveSuccess(true);
      // Close the modal
      dispatch(showSecretMappingModal(false));
    } catch (error) {
      console.error('Failed to save secret mappings:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save secret mappings');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles modal cancellation
   */
  const handleCancel = () => {
    dispatch(showSecretMappingModal(false));
    setSaveSuccess(false);
    setSaveError(null);
  };

  /**
   * Handles context change and resets environment variable
   */
  const handleContextChange = (secretName: string, context: string) => {
    updateMapping(secretName, 'context', context);
    updateMapping(secretName, 'envVarName', '');
  };

  /**
   * Clears the context and environment variable mapping for a secret
   */
  const clearMapping = (secretName: string) => {
    updateMapping(secretName, 'context', '');
    updateMapping(secretName, 'envVarName', '');
  };

  /**
   * Opens the Hub Secrets page in the UserProfile modal
   */
  const handleOpenHubSecrets = () => {
    // Close the current modal first
    // dispatch(showSecretMappingModal(false));
    // Open the UserProfile modal and navigate to hub-secrets page
    dispatch(showUserProfileModal('hub-secrets'));
  };

  /**
   * Table columns configuration
   */
  const columns: ColumnsType<SecretMapping> = [
    {
      title: t('Column.RequiredSecret'),
      dataIndex: 'secretName',
      key: 'secretName',
      width: 200,
      render: (secretName: string) => (
        <code className={styles.SecretName}>{secretName.toUpperCase()}</code>
      ),
    },
    {
      title: t('Column.Context'),
      dataIndex: 'context',
      key: 'context',
      width: 150,
      render: (context: string, record: { secretName: string; context: string; envVarName: string; createdAt: string }) => {
        const availableContexts = getAvailableContexts();
        return (
          <Select
            placeholder={t('Placeholder.SelectContext')}
            value={context || undefined}
            onChange={(value) => handleContextChange(record.secretName, value)}
            style={{ width: '100%' }}
            notFoundContent={t('ContextDropdown.NoContextsFound')}
          >
            {availableContexts.map(contextName => (
              <Option key={contextName} value={contextName}>
                {contextName}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: t('Column.EnvironmentVariable'),
      dataIndex: 'envVarName',
      key: 'environmentVariable',
      width: 200,
      render: (envVarName: string, record: { secretName: string; context: string; envVarName: string; createdAt: string }) => {
        const availableEnvVars = getAvailableEnvVars(record.context);
        
        return (
          <Select
            placeholder={t('Placeholder.SelectVariable')}
            value={envVarName || undefined}
            onChange={(value) => updateMapping(record.secretName, 'envVarName', value)}
            style={{ width: '100%' }}
            disabled={!record.context}
            notFoundContent={t('Placeholder.NoVariablesFound')}
          >
            {availableEnvVars.map(envVarInfo => (
              <Option key={envVarInfo.key} value={envVarInfo.key}>
                {envVarInfo.key.toUpperCase()}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: tCommon('Actions'),
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record: SecretMapping) => (
        <Space>
          {record.hubServiceName && (
            <Tooltip title={record.rememberMapping ? t('BookmarkTooltip.Saved') : t('BookmarkTooltip.Remember')}>
              <Button
                type="text"
                size="small"
                icon={record.rememberMapping ? <Icon component={BookmarkTicked} className={styles.BookmarkIconTicked}/> : <Icon component={BookmarkIcon} />}
                onClick={() => updateMapping(record.secretName, 'rememberMapping', !record.rememberMapping)}
              />
            </Tooltip>
          )}

          <Button
            type="text"
            size="small"
            onClick={() => clearMapping(record.secretName)}
            disabled={!record.context && !record.envVarName}
            title={t('Actions.ClearMapping')}
          >
            {tCommon('Clear')}
          </Button>
        </Space>
      ),
    },
  ];

  // Load data when modal becomes visible
  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible, recipeUuid]);

  return (
    <RoundedModal
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      title={t('Title')}
      width={800}
      okBtnLabel={tCommon('Save')}
      disableOkButton={loading}
      closeOnMaskClick={false}
      cancelBtnLabel={tCommon('Close')}
    >
      <div className={styles.Container}>
        {saveError && (
          <Alert
            description={saveError}
            type="error"
            showIcon
            closable
            onClose={() => setSaveError(null)}
            className={styles.ErrorAlert}
          />
        )}
        {saveSuccess && (
          <Alert
            message={t('Success.MappingsSaved')}
            description={t('Success.MappingsSavedDescription')}
            type="success"
            showIcon
            closable
            onClose={() => setSaveSuccess(false)}
            className={styles.SuccessAlert}
          />
        )}
        <p className={styles.Instructions}>{t('Instructions')}</p>
        
        <div className={styles.HubSecretsLink}>
          <Button
            type="link"
            icon={<SettingOutlined />}
            onClick={handleOpenHubSecrets}
            className={styles.HubSecretsButton}
          >
            {t('LinkToHubSecrets')}
          </Button>
        </div>
        
        <Spin spinning={loading} indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />}>
          <Table
            columns={columns}
            dataSource={secretMappings}
            pagination={false}
            rowKey="secretName"
            size="small"
            className={styles.Table}
          />
        </Spin>
        
        <Alert
          message={t('Persistence.Title')}
          description={t('Persistence.Description')}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </div>
    </RoundedModal>
  );
};

export default SecretMappingModal;
